import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { fetchAppConfig } from "@/lib/appConfig";
import { Home, QrCode, User, Bell, Video, Newspaper, MessageSquare, Headphones } from "lucide-react";
import { computeTierStyle, tierSurfaceStyle } from "@/lib/tierStyle";

const MYERBA_LOGO = "/api/files/branding/myerba.png";
const TAGNPAY_LOGO = "https://customer-assets.emergentagent.com/job_images-34/artifacts/tdji2o49_tagnpay-logo-new.png";

export default function CustomerLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasUnread, setHasUnread] = useState(false);
  const [fonts, setFonts] = useState(null);
  const [appCfg, setAppCfg] = useState(null);

  // Point 17: apply admin-configured font sizes (heading / subheading / body) to the customer app.
  useEffect(() => {
    fetchAppConfig().then((cfg) => { setFonts(cfg.fonts || null); setAppCfg(cfg); });
  }, []);

  // Point 11: show the red dot only when there is an unread notification.
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await API.get("/notifications/unread");
        if (active) setHasUnread(!!data.unread);
      } catch { /* ignore */ }
    };
    check();
    const onUpdated = () => check();
    window.addEventListener("notifications-updated", onUpdated);
    return () => { active = false; window.removeEventListener("notifications-updated", onUpdated); };
  }, [location.pathname]);

  const firstName = (user?.full_name || "").trim().split(/\s+/)[0] || "";
  const tier = user?.tier || "Silver";
  // Point 40: profile button surface mirrors the Home tier card exactly
  // (config image / colour / gradient), instead of a separate hard-coded slab.
  const ts = computeTierStyle(tier, 0, appCfg);
  const surface = tierSurfaceStyle(ts);
  const profileUseGradient = !surface;
  const profileIconColor = ts.isDark ? "#FFFFFF" : "#1A2530";

  // Bottom nav: [Home] [Videos] [Scan (center)] [News] [Enquire]
  const navItems = [
    { path: "/app", icon: Home, label: "Home", end: true },
    { path: "/app/videos", icon: Video, label: "Videos" },
    { path: "/app/scan", icon: QrCode, label: "Scan", primary: true },
    { path: "/app/insights", icon: Newspaper, label: "News" },
    { path: "/app/enquiry", icon: MessageSquare, label: "Enquire" },
  ];

  // Point 17: build scoped CSS overriding heading/subheading/body sizes only when configured.
  const fontCss = (() => {
    if (!fonts) return "";
    const rules = [];
    if (fonts.heading) rules.push(`.myerba-fonts h1{font-size:${fonts.heading}px !important;line-height:1.2 !important;}`);
    if (fonts.subheading) rules.push(`.myerba-fonts h2,.myerba-fonts h3{font-size:${fonts.subheading}px !important;line-height:1.3 !important;}`);
    if (fonts.body) rules.push(`.myerba-fonts p,.myerba-fonts .app-body-text{font-size:${fonts.body}px !important;line-height:1.5 !important;}`);
    return rules.join("");
  })();

  return (
    <div className="myerba-fonts min-h-screen bg-[#F4F7FA] pb-20" data-testid="customer-layout">
      {fontCss && <style>{fontCss}</style>}
      {/* Header: MyErba logo (left) + Profile + Notification (right) */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-gray-200/60" data-testid="customer-header">
        <div className="max-w-lg mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 -ml-1 min-w-0">
            <button
              onClick={() => navigate("/app")}
              className="p-1 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
              data-testid="header-logo-btn"
              aria-label="Home"
            >
              <img src={MYERBA_LOGO} alt="MyErba" className="h-10 w-auto object-contain" />
            </button>
            {firstName && (
              <button
                onClick={() => navigate("/app/profile")}
                className="flex flex-col min-w-0 leading-tight p-1 rounded-lg hover:bg-gray-50 transition-colors text-left"
                data-testid="header-welcome"
                aria-label="Open profile"
              >
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Welcome</span>
                <span className="text-sm font-bold text-[#008A99] truncate max-w-[120px]">{firstName}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate("/app/complaints")}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
              title="Customer Support"
              data-testid="customer-support-btn"
            >
              <Headphones className="w-5 h-5" />
            </button>
            <button onClick={() => navigate("/app/notifications")} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 relative" data-testid="notifications-btn">
              <Bell className="w-5 h-5" />
              {hasUnread && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full" data-testid="notif-unread-dot" />}
            </button>
            <NavLink
              to="/app/profile"
              className={`p-2 rounded-lg transition-all relative overflow-hidden shadow ${profileUseGradient ? `bg-gradient-to-br ${ts.gradientClass}` : ""}`}
              style={surface || undefined}
              data-testid="profile-header-link"
              aria-label={`${tier} profile`}
            >
              <User className="w-5 h-5 relative z-10" style={{ color: profileIconColor }} />
              <span className="absolute inset-0 rounded-lg pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
            </NavLink>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-5"><Outlet /></main>

      <div className="max-w-lg mx-auto px-4 pb-24">
        <div className="flex items-center justify-center gap-2 py-3 opacity-40">
          <span className="text-[9px] text-gray-500 uppercase tracking-[0.15em] font-semibold">Powered by</span>
          <img src={TAGNPAY_LOGO} alt="TagNPay" className="h-3.5 opacity-80" />
        </div>
      </div>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200/60 px-2 py-1.5" data-testid="bottom-nav">
        <div className="max-w-lg mx-auto flex items-end justify-around">
          {navItems.map((item) => {
            if (item.primary) {
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className="relative -mt-6 flex flex-col items-center gap-1"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {({ isActive }) => (
                    <>
                      <span className={`w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 ${isActive ? "bg-gradient-to-b from-[#008A99] to-[#004A52] shadow-[#008A99]/40 scale-105" : "bg-[#008A99] shadow-[#008A99]/30 hover:scale-105"}`}>
                        <item.icon className="w-6 h-6" />
                      </span>
                      <span className={`text-[10px] font-bold ${isActive ? "text-[#008A99]" : "text-[#008A99]/80"}`}>{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-xl transition-all duration-200 min-w-[56px] ${isActive ? "text-[#008A99] bg-[#E0F4F5]" : "text-gray-400 hover:text-gray-600"}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-semibold">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
